import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@material-ui/core';
import { Alert, AlertTitle, Color } from '@material-ui/lab';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
  Warning as WarningIcon
} from '@material-ui/icons';

import { FetchError } from '../../../../common/api/api';
import CollapsibleRow from '../../../../common/CollapsibleRow';
import WrappedTableCell from '../../../../common/WrappedTableCell';
import ConfirmDialog from '../../../../common/dialogs/ConfirmDialog';
import { useGetTaskTypes } from '../../../task-types/TaskType.hooks';
import {
  TaskType,
  TaskTypeEntity
} from '../../../task-types/TaskType.interface';
import TaskTypeSelector, {
  TaskTypeSelectorProps
} from '../../../task-types/components/TaskTypeSelector';
import {
  FileDto,
  ReferenceData,
  ReferenceMetadata
} from '../../Document.interface';
import {
  FileUploadItem,
  FileUploadManager,
  FileUploadProgress
} from './DocumentFolderUpload.hooks';
import { FileUploader, useFileUploader } from './useFileUploader';

const StyledBox = styled(Box)`
  .inline-icon {
    vertical-align: middle;
    margin-left: 4px;
    margin-right: 4px;
  }
`;

const StyledCollapsibleRow = styled(CollapsibleRow)`
  .secondary {
    color: ${props => props.theme.palette.action.active};
  }
  .success-icon {
    color: ${props => props.theme.palette.success.main};
  }
  .error-icon {
    color: ${props => props.theme.palette.error.main};
  }
  .warning-icon {
    color: ${props => props.theme.palette.warning.main};
  }
  .progress {
    position: relative;
    margin-left: 14px;
    .bg-progress {
      color: ${props => props.theme.palette.primary.light};
    }
    .fg-progress {
      position: absolute;
      left: 0;
    }
  }
`;

export interface RenderActionsProps {
  progress: FileUploadProgress;
  uploadAllButton: React.ReactNode;
  cancelAll: () => void;
  uploadAll: () => void;
}

export interface DocumentFolderUploadListProps {
  folder: FileDto;
  manager: FileUploadManager;
  referenceData?: ReferenceData;
  referenceTaskId?: number;
  referenceMetadata?: ReferenceMetadata;
  renderActions?: (props: RenderActionsProps) => React.ReactNode;
}

export const DocumentFolderUploadList: React.FC<
  DocumentFolderUploadListProps
> = (props: DocumentFolderUploadListProps) => {
  const {
    folder,
    manager,
    referenceData,
    referenceTaskId,
    referenceMetadata,
    renderActions
  } = props;
  const itemsRef = useRef<DocumentFolderUploadItemRef[]>([]);
  const [removeItem, setRemoveItem] = useState<FileUploadItem | undefined>();
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [commonTaskType, setCommonTaskType] = useState<TaskType | undefined>();
  const [isUploadingAll, setIsUploadingAll] = useState(false);
  const [{ data, isLoading, error }, findTaskTypes] = useGetTaskTypes();
  const uploader = useFileUploader();
  const { progress } = manager;
  const parentId = !folder.id || folder.id <= 0 ? null : folder.id;

  const severity: Color = useMemo(() => {
    const { total, success, error } = progress;
    if (total === success) {
      return 'success';
    } else if (total === error) {
      return 'error';
    } else if (error > 0) {
      return 'warning';
    } else {
      return 'info';
    }
  }, [progress]);

  const handleRef = (ref: DocumentFolderUploadItemRef | null, file: File) => {
    const index: number = itemsRef.current.findIndex(
      (item: DocumentFolderUploadItemRef) => item.file === file
    );
    // if ref exists, save if it doesn't exist
    // otherwise, remove it if it exists
    if (ref) {
      if (index === -1) {
        itemsRef.current.push(ref);
      } else {
        itemsRef.current.splice(index, 1, ref);
      }
    } else if (index > -1) {
      itemsRef.current.splice(index, 1);
    }
  };

  const cancelAll = useCallback(() => uploader.clear(true), [uploader]);

  const uploadAll = useCallback(async () => {
    setIsUploadingAll(true);
    const files = itemsRef.current.map(item => item.file);
    await uploader.uploadAsync(files, (file, presignedUrl) => {
      // assert that taskType exists
      const item = itemsRef.current.find(item => item.file === file);
      return {
        name: file.name,
        size: file.size,
        mimeType: file.type,
        parentId,
        referenceData,
        referenceTaskId,
        referenceMetadata,
        taskTypeName: item?.taskType.name || '',
        storageKey: presignedUrl.storageKey
      };
    });
    setIsUploadingAll(false);
  }, [parentId, referenceTaskId, referenceData, referenceMetadata, uploader]);

  useEffect(() => {
    findTaskTypes({ targetEntity: TaskTypeEntity.File });
  }, [findTaskTypes]);

  useEffect(() => {
    data && setTaskTypes(data);
  }, [data]);

  const uploadAllButton: React.ReactNode = useMemo(() => {
    const { pending } = progress;
    return pending <= 0 ? null : (
      <Button
        color='primary'
        variant='contained'
        onClick={uploadAll}
        disabled={isUploadingAll}
        endIcon={<CloudUploadOutlinedIcon />}
      >
        {pending === 1 ? 'Upload File' : `Upload All ${pending} Files`}
      </Button>
    );
  }, [isUploadingAll, progress, uploadAll]);

  return (
    <>
      <Box mx={2} mb={1}>
        <TaskTypeSelector
          options={taskTypes}
          value={commonTaskType?.name}
          disabled={isUploadingAll}
          textFieldProps={{
            label: 'Select Document Type for all items',
            placeholder: 'Choose a Document Type'
          }}
          onChange={(taskType: TaskType | null) => {
            setCommonTaskType(taskType || undefined);
          }}
        />
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Document Type</TableCell>
              <TableCell>Progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {manager.items.map((item: FileUploadItem) => (
              <DocumentFolderUploadItem
                key={item.id}
                item={item}
                uploader={uploader}
                parentId={parentId}
                taskType={commonTaskType}
                disabled={isUploadingAll}
                open={selectedId === item.id}
                referenceData={referenceData}
                referenceTaskId={referenceTaskId}
                referenceMetadata={referenceMetadata}
                selectorProps={{
                  isLoading,
                  options: taskTypes,
                  errorMessage: error?.message
                }}
                setOpen={(open: boolean) => {
                  setSelectedId(open ? item.id : undefined);
                }}
                onRemove={() => setRemoveItem(item)}
                onError={() => manager.setStatus(item.id, 'error')}
                onCreate={() => manager.setStatus(item.id, 'success')}
                onLoad={(isLoading: boolean) => {
                  if (isLoading && item.status === 'pending') {
                    manager.setStatus(item.id, 'uploading');
                  }
                }}
                onIncomplete={(isIncomplete: boolean) => {
                  // set as pending if currently incomplete
                  const status: FileUploadItem['status'] | undefined =
                    isIncomplete
                      ? 'incomplete'
                      : item.status === 'incomplete'
                      ? 'pending'
                      : undefined;
                  status && manager.setStatus(item.id, status);
                  status === 'pending' && uploader.remove(item.file);
                }}
                innerRef={(ref: DocumentFolderUploadItemRef | null) => {
                  handleRef(ref, item.file);
                }}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <StyledBox mx={3} mt={1}>
        {progress.total === progress.pending + progress.incomplete ? (
          <Alert severity='info'>
            Click on the upload icon{' '}
            <CloudUploadOutlinedIcon color='primary' className='inline-icon' />{' '}
            to start and confirm the files for uploading.
          </Alert>
        ) : (
          <Alert severity={severity}>
            <AlertTitle>
              Uploading <strong>{progress.total}</strong> files to{' '}
              {parentId === null ? (
                <>
                  the <strong>root</strong> folder.
                </>
              ) : (
                <>
                  <strong>{folder?.name || '?'}</strong>.
                </>
              )}
            </AlertTitle>
            Progress: <strong>{progress.success}</strong> uploaded.{' '}
            <strong>{progress.error}</strong> failed.{' '}
            <strong>{progress.uploading}</strong> uploading.{' '}
            <strong>{progress.pending}</strong> pending.{' '}
            <strong>{progress.incomplete}</strong> incomplete.
          </Alert>
        )}
      </StyledBox>

      {renderActions?.({ progress, uploadAllButton, cancelAll, uploadAll })}

      <ConfirmDialog
        open={!!removeItem}
        title='Remove File'
        message={`Are you sure you want to remove the file "${
          removeItem?.file.name || '?'
        }" from upload?`}
        onCancel={() => setRemoveItem(undefined)}
        onConfirm={() => {
          setRemoveItem(undefined);
          if (removeItem) {
            manager.remove(removeItem.id);
          }
        }}
      />
    </>
  );
};

export default DocumentFolderUploadList;

interface AlertMessage {
  severity?: Color;
  message: React.ReactNode;
}

export interface DocumentFolderUploadItemRef {
  file: File;
  taskType: TaskType;
}

export interface DocumentFolderUploadItemProps {
  item: FileUploadItem;
  open: boolean;
  uploader: FileUploader;
  parentId: number | null;
  taskType?: TaskType;
  disabled?: boolean;
  referenceData?: ReferenceData;
  referenceTaskId?: number;
  referenceMetadata?: ReferenceMetadata;
  selectorProps?: Partial<TaskTypeSelectorProps>;
  innerRef?: React.RefCallback<DocumentFolderUploadItemRef>;
  setOpen: (open: boolean) => void;
  onLoad?: (isLoading: boolean) => void;
  onError?: (error: FetchError) => void;
  onCreate?: (file: FileDto) => void;
  onRemove?: () => void;
  onIncomplete?: (isIncomplete: boolean) => void;
}

export const DocumentFolderUploadItem: React.FC<
  DocumentFolderUploadItemProps
> = (props: DocumentFolderUploadItemProps) => {
  const {
    open,
    item,
    uploader,
    parentId,
    disabled = false,
    taskType: commonTaskType,
    referenceData,
    referenceTaskId,
    referenceMetadata,
    selectorProps,
    innerRef,
    setOpen,
    onLoad,
    onError,
    onCreate,
    onRemove,
    onIncomplete
  } = props;
  const { file } = item;
  const [taskType, setTaskType] = useState<TaskType | undefined>();
  const [alertMessage, setAlertMessage] = useState<AlertMessage | undefined>();
  const { options: taskTypes, isLoading, errorMessage } = selectorProps || {};

  const { uploadItem, isUploading } = useMemo(() => {
    const uploadItem = uploader.find(file);
    const isUploading =
      !!uploadItem &&
      ['loading', 'uploading', 'creating'].includes(uploadItem.status);
    return { uploadItem, isUploading };
  }, [file, uploader]);
  const error = uploadItem?.error;
  const newFile = uploadItem?.fileDto;
  const progress = uploadItem?.progress || 0;

  const doUpload = () => {
    if (!taskType) {
      return;
    }
    uploader.upload([file], (file, presignedUrl) => {
      return {
        name: file.name,
        size: file.size,
        mimeType: file.type,
        parentId,
        referenceData,
        referenceTaskId,
        referenceMetadata,
        taskTypeName: taskType.name,
        storageKey: presignedUrl.storageKey
      };
    });
  };

  useEffect(() => {
    innerRef?.(taskType ? { file, taskType } : null);
  }, [file, taskType, innerRef]);

  // clear innerRef on unmount
  useEffect(() => {
    return () => innerRef?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // set selected TaskType of parent selector
  useEffect(() => {
    if (!newFile && commonTaskType) {
      setTaskType(commonTaskType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonTaskType]);

  useEffect(() => {
    // if no TaskType, set as incomplete
    onIncomplete?.(!taskType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskType]);

  useEffect(() => {
    onLoad?.(isUploading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading]);

  useEffect(() => {
    if (newFile) {
      setAlertMessage({
        severity: 'success',
        message: `Document ${newFile.name} created.`
      });
      onCreate?.(newFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newFile]);

  useEffect(() => {
    if (error) {
      setAlertMessage({
        severity: 'error',
        message: error.message || 'Unable to upload document.'
      });
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    // reset state when changing back to pending
    if (item.status === 'pending') {
      setAlertMessage(undefined);
    }
  }, [item.status]);

  const collapsedContent: React.ReactNode = (
    <Box padding={2}>
      <Box>
        <TaskTypeSelector
          options={taskTypes}
          value={taskType?.name}
          disabled={disabled || !!newFile}
          isLoading={isLoading}
          errorMessage={errorMessage}
          textFieldProps={{
            label: 'Select Document Type',
            placeholder: 'Choose a Document Type'
          }}
          onChange={(taskType: TaskType | null) => {
            setTaskType(taskType || undefined);
          }}
        />
      </Box>
      {alertMessage && (
        <Box mt={2}>
          <Alert severity={alertMessage.severity}>{alertMessage.message}</Alert>
        </Box>
      )}
    </Box>
  );

  return (
    <StyledCollapsibleRow
      hideAction
      open={open}
      colSpan={3}
      collapsedContent={collapsedContent}
      rowProps={{ hover: true, onClick: () => setOpen(!open) }}
    >
      <WrappedTableCell>{file.name}</WrappedTableCell>
      <WrappedTableCell
        title='Click to update'
        className={!taskType ? 'secondary' : undefined}
      >
        {taskType
          ? `${taskType.label} (${taskType.prefix})`
          : 'Click to update'}
      </WrappedTableCell>
      <TableCell className='actions'>
        {newFile || error ? (
          <IconButton
            onClick={event => {
              event.stopPropagation();
              setOpen(!open);
            }}
          >
            {error ? (
              <ErrorOutlineIcon className='error-icon' />
            ) : (
              <CheckCircleOutlineIcon className='success-icon' />
            )}
          </IconButton>
        ) : !isUploading ? (
          <>
            {!taskType ? (
              <Tooltip title='Missing Document Type'>
                <IconButton>
                  <WarningIcon className='warning-icon' />
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton
                color='primary'
                disabled={disabled || !taskType}
                onClick={event => {
                  event.stopPropagation();
                  doUpload();
                }}
              >
                <CloudUploadOutlinedIcon />
              </IconButton>
            )}
            <IconButton
              disabled={disabled}
              onClick={event => {
                event.stopPropagation();
                onRemove?.();
              }}
            >
              <CloseIcon />
            </IconButton>
          </>
        ) : (
          <span className='progress'>
            <CircularProgress
              size={20}
              value={100}
              thickness={6}
              variant='determinate'
              className='bg-progress'
            />
            <CircularProgress
              size={20}
              thickness={6}
              value={progress}
              className='fg-progress'
              variant={
                !progress || progress === 100 ? 'indeterminate' : 'determinate'
              }
            />
          </span>
        )}
      </TableCell>
    </StyledCollapsibleRow>
  );
};
